/* /training/audio-demo — Audio + Photo demo of the kinetic-lesson format.
   Uses Mike's existing WELCOME VIDEO SCRIPT audio + head coach photo + logo. */
import { useEffect } from "react";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import KineticLesson from "../components/KineticLesson";
import { CheckCircle2 } from "lucide-react";

const G   = "#2E9F3A";
const RED = "#C9202E";
const INK = "#0F172A";

const AUDIO  = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/xwt3qc33_WELCOME%20VIDEO%20SCRIPT%2028%20APRIL%202026%20%281%29.m4a";
const POSTER = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/nnqiq1ad_MICHAEL%20BOYD%20FAITHARAMA.COM%20HEAD%20COACH.png";
const BANNER = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/nhmux381_FAITHARAMA.COM%20LOGO.png";

/* Placeholder kinetic script. When you tell me the actual welcome-audio
   timestamps, I'll resync these cards to match what you're saying. */
const SCRIPT = [
  { time: 0,   text: "WELCOME TO FAITHARAMA",         kind: "title",   subtitle: "From Mike Boyd · Head Coach" },
  { time: 8,   text: "Faith. Wellness. Income.",      kind: "tagline" },
  { time: 16,  text: "All in one place.",             kind: "tagline" },
  { time: 25,  text: "Built for everyone.",           kind: "tagline" },
  { time: 34,  text: "Believer or not.",              kind: "tagline" },
  { time: 43,  text: "Clean. Family-friendly.",       kind: "tagline" },
  { time: 53,  text: "12 Powerful Builders.",         kind: "stat" },
  { time: 63,  text: "One Login.",                    kind: "tagline" },
  { time: 72,  text: "50/50 Commission.",             kind: "stat",    subtitle: "Same rate for everyone — trial or paid." },
  { time: 84,  text: "If you can post on Facebook,",  kind: "quote" },
  { time: 92,  text: "you can do this.",              kind: "quote" },
  { time: 102, text: "7 Days Free.",                 kind: "stat" },
  { time: 112, text: "No credit card. No catch.",     kind: "tagline" },
  { time: 124, text: "See you on the inside.",        kind: "quote" },
  { time: 138, text: "Faitharama.com",                kind: "title" },
];

export default function AudioLessonDemoPage() {
  useEffect(() => {
    document.title = "Audio Lesson Demo — Faitharama";
  }, []);
  return (
    <div className="min-h-screen bg-white">
      <SiteNav />

      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-5xl mx-auto px-5 md:px-8">

          <div className="text-center mb-7">
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2" style={{ color: RED }}>
              ★ Format Demo — Audio + Photo
            </div>
            <h1 className="font-serif-display uppercase leading-[0.98]"
                style={{ color: INK, fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}>
              Send Audio. Send A Photo. <br/>
              <span style={{ color: G }}>I Sync It All Up.</span>
            </h1>
            <p className="text-base md:text-lg mt-4 max-w-2xl mx-auto" style={{ color: INK }}>
              This is the same player you saw on the video demo — but using your <strong>Welcome audio</strong> + <strong>head coach photo</strong> + <strong>Faitharama logo banner</strong>. The photo slowly zooms so it doesn&apos;t feel static.
            </p>
            <p className="text-sm italic mt-3" style={{ color: INK, opacity: 0.7 }}>
              Hit the green play button. The first click will unmute your audio.
            </p>
          </div>

          <KineticLesson
            audioUrl={AUDIO}
            posterUrl={POSTER}
            bannerUrl={BANNER}
            title="Welcome to Faitharama"
            subtitle="Audio + Photo Demo"
            duration={155}
            cards={SCRIPT}
          />

          {/* What I need from you */}
          <div className="mt-10 rounded-2xl border-4 p-6 md:p-8 shadow-md"
               style={{ borderColor: G, background: "linear-gradient(180deg,#F0FAF1 0%,#FFFFFF 100%)" }}>
            <h2 className="font-serif-display text-2xl md:text-3xl uppercase mb-4" style={{ color: INK }}>
              Per Lesson, Send Me Three Things:
            </h2>
            <ul className="space-y-3">
              {[
                { t: "1. Audio file",  d: "MP3, M4A, or WAV. You talking — 2 to 3 minutes per lesson." },
                { t: "2. A photo of you", d: "Chest-up or full-body, looking at camera. One per lesson — or one master photo I can reuse for all 12." },
                { t: "3. Banner (optional)", d: "Faitharama logo or a course-title strip you want pinned at the top." },
              ].map((b) => (
                <li key={b.t} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: G }} />
                  <div>
                    <div className="font-bold text-base md:text-lg" style={{ color: INK }}>{b.t}</div>
                    <div className="text-base leading-relaxed" style={{ color: INK, opacity: 0.85 }}>{b.d}</div>
                  </div>
                </li>
              ))}
            </ul>

            <p className="mt-5 text-base md:text-lg italic" style={{ color: INK }}>
              Tell me which lesson it&apos;s for (e.g., "Lesson 1 — Funnel Builder") and I&apos;ll sync the kinetic headlines to your audio timestamps. <strong>One lesson, one upload</strong> — that&apos;s the whole workflow.
            </p>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
